// src/components/theme/elements/ThemeBar.js

import React from 'react';
import { Rect, Group } from 'react-konva';
import Checkbox from '@mui/material/Checkbox';
import PropTypes from 'prop-types';
import { generateColorGradient } from '../colorUtils'; // Ensure correct import path
import { barUsageOptions } from '../ThemeLists';
import CustomColorPicker from '../CustomColorPicker';
import styles from './styles.module.css';
import { clamp, snapToGridFn } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';


/**
 * ThemeBarSettings Component
 * Provides UI controls to configure the ThemeBar properties.
 */
export const ThemeBarSettings = ({ element, updateElement }) => {
  // Handlers for various input changes with clamping and validation
  const handleXChange = (e) => {
    let newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue)) {
      newValue = clamp(newValue, 0, 479); // Clamp between 0 and 479
      updateElement(element.id, 'x', newValue);
    }
  };

  const handleYChange = (e) => {
    let newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue)) {
      newValue = clamp(newValue, 0, 319); // Clamp between 0 and 319
      updateElement(element.id, 'y', newValue);
    }
  };

  const handleWidthChange = (e) => {
    let newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue >= 2) {
      updateElement(element.id, 'barWidth', newValue);
    }
  };

  const handleHeightChange = (e) => {
    let newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue >= 2) {
      updateElement(element.id, 'barHeight', newValue);
    }
  };

  const handleMarginChange = (e) => {
    let newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue >= 0) {
      updateElement(element.id, 'margin', newValue);
    }
  };

  const handleNumberOfBarsChange = (e) => {
    let newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue > 0) {
      updateElement(element.id, 'numberOfBars', newValue);
    }
  };

  const handleStartColorChange = (value) => {
    updateElement(element.id, 'startColor', value);
  };

  const handleEndColorChange = (value) => {
    updateElement(element.id, 'endColor', value);
  };

  const handleDisabledColorChange = (value) => {
    updateElement(element.id, 'disabledColor', value);
  };

  const handleStartValueChange = (e) => {
    let newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue)) {
      updateElement(element.id, 'startValue', newValue);
    }
  };

  const handleEndValueChange = (e) => {
    let newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue)) {
      updateElement(element.id, 'endValue', newValue);
    }
  };

  const handleTestValueChange = (e) => {
    let newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue)) {
      updateElement(element.id, 'testValue', newValue);
    }
  };

  const handleUsageChange = (e) => {
    updateElement(element.id, 'usage', e.target.value);
  };

  const handleRotationChange = (e) => {
    const isChecked = e.target.checked;
    const newRotation = isChecked ? 90 : 0;
    updateElement(element.id, 'rotation', newRotation);
  };

  return (
    <div>
      {/* X Position */}
      <label>
        X Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_WIDTH - 1}
          value={element.x}
          onChange={handleXChange}
          aria-label="Set X Position"
        />
      </label>

      {/* Y Position */}
      <label>
        Y Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_HEIGHT - 1}
          value={element.y}
          onChange={handleYChange}
          aria-label="Set Y Position"
        />
      </label>

      {/* Bar Width */}
      <label>
        Bar Width:
        <input
          className={styles.inputNumber}
          type="number"
          min="2"
          value={element.barWidth}
          onChange={handleWidthChange}
          aria-label="Set Bar Width"
        />
      </label>

      {/* Bar Height */}
      <label>
        Bar Height:
        <input
          className={styles.inputNumber}
          type="number"
          min="2"
          value={element.barHeight}
          onChange={handleHeightChange}
          aria-label="Set Bar Height"
        />
      </label>

      {/* Pixel Spacing */}
      <label>
        Pixel Spacing:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          value={element.margin}
          onChange={handleMarginChange}
          aria-label="Set Pixel Spacing"
        />
      </label>

      {/* Number of Bars */}
      <label>
        Number of Bars:
        <input
          className={styles.inputNumber}
          type="number"
          min="1"
          value={element.numberOfBars}
          onChange={handleNumberOfBarsChange}
          aria-label="Set Number of Bars"
        />
      </label>

      {/* Min Value Color */}
      <CustomColorPicker
        label="Min value color"
        color={element.startColor}
        onChange={handleStartColorChange}
        aria-label="Set Min Value Color"
      />

      {/* Max Value Color */}
      <CustomColorPicker
        label="Max value color"
        color={element.endColor}
        onChange={handleEndColorChange}
        aria-label="Set Max Value Color"
      />

      {/* Disabled Color */}
      <CustomColorPicker
        label="Disabled Color"
        color={element.disabledColor}
        onChange={handleDisabledColorChange}
        aria-label="Set Disabled Color"
      />

      {/* Start Value */}
      <label>
        Min Value:
        <input
          className={styles.inputText}
          type="number"
          value={element.startValue}
          onChange={handleStartValueChange}
          aria-label="Set Min Value"
        />
      </label>

      {/* End Value */}
      <label>
        Max Value:
        <input
          className={styles.inputText}
          type="number"
          value={element.endValue}
          onChange={handleEndValueChange}
          style={{ marginLeft: '10px' }}
          aria-label="Set Max Value"
        />
      </label>

      {/* Test Value */}
      <label>
        Test Value:
        <input
          className={styles.inputText}
          type="number"
          value={element.testValue}
          onChange={handleTestValueChange}
          style={{ marginLeft: '10px' }}
          aria-label="Set Test Value"
        />
      </label>

      {/* Rotation Checkbox */}
      <label style={{ marginTop: '10px', display: 'block' }}>
        Rotate 90°:
        <Checkbox
          checked={element.rotation === 90}
          onChange={handleRotationChange}
          style={{ marginLeft: '10px' }}
          aria-label="Toggle Rotation"
        />
      </label>

      {/* Usage Dropdown */}
      <label style={{ marginTop: '10px', display: 'block' }}>
        Usage:
        <select
          className={styles.select200}
          value={element.usage}
          onChange={handleUsageChange}
          style={{ marginLeft: '10px' }}
          aria-label="Set Usage"
        >
          {barUsageOptions.map((option) => (
            <option key={`bar-usage-${option.value}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
};

ThemeBarSettings.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    usage: PropTypes.string.isRequired,
    barWidth: PropTypes.number.isRequired,
    barHeight: PropTypes.number.isRequired,
    margin: PropTypes.number.isRequired,
    numberOfBars: PropTypes.number.isRequired,
    startColor: PropTypes.string.isRequired,
    endColor: PropTypes.string.isRequired,
    disabledColor: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    startValue: PropTypes.number.isRequired,
    endValue: PropTypes.number.isRequired,
    testValue: PropTypes.number.isRequired,
    rotation: PropTypes.number.isRequired,
  }).isRequired,
  updateElement: PropTypes.func.isRequired,
};

//////////////////////////////////////////////////

/**
 * ThemeBar Component
 * Renders a bar graph indicator with draggable functionality.
 */
const ThemeBar = ({
  element,
  selectedElement,
  setSelectedElement,
  updateElement,
  gridSize,
  snapToGridEnabled,
  render,editAllowed,
  scale,
  screenWidth,    // Canvas width in unscaled units (e.g., 480)
  screenHeight,   // Canvas height in unscaled units (e.g., 320)
}) => {
  const {
    x = 50,
    y = 50,
    barWidth = 5,
    barHeight = 31,
    margin = 2,
    numberOfBars = 12,
    startColor = '#00FF40',
    endColor = '#FF0000',
    disabledColor = '#444444',
    testValue = 0,
    startValue = 0,
    endValue = 100,
    rotation = 0,
  } = element;

  // Generate the color palette based on start and end colors
  const colorPalette = generateColorGradient(startColor, endColor, numberOfBars);

  // Calculate the number of filled bars based on testValue
  let filledBars = 0;
  if (startValue !== endValue) {
    const clampedValue = clamp(testValue, startValue, endValue);
    const proportion = (clampedValue - startValue) / (endValue - startValue);
    filledBars = Math.round(proportion * numberOfBars);
  } else {
    filledBars = testValue >= startValue ? numberOfBars : 0;
  }

  // Calculate total dimensions based on rotation
  const totalWidth =
    rotation === 0
      ? numberOfBars * (barWidth + margin) - margin
      : barHeight;
  const totalHeight =
    rotation === 0
      ? barHeight
      : numberOfBars * (barWidth + margin) - margin;

  /**
   * Calculate the clamped position to keep the element within the screen.
   * Ensures the entire bar graph remains within the canvas boundaries.
   * @param {number} desiredX - Desired X position in unscaled units.
   * @param {number} desiredY - Desired Y position in unscaled units.
   * @returns {Object} - Clamped position with integer x and y.
   */
  const calculateClampedPosition = (desiredX, desiredY) => {
    // Snap to grid if enabled
    let snappedX = snapToGridFn(Math.round(desiredX), gridSize, snapToGridEnabled);
    let snappedY = snapToGridFn(Math.round(desiredY), gridSize, snapToGridEnabled);

    // Clamp positions within canvas boundaries
    const clampedX = clamp(snappedX, 0, screenWidth - totalWidth);
    const clampedY = clamp(snappedY, 0, screenHeight - totalHeight);

    console.log(snappedX, snappedY, clampedX, clampedY);

    // Ensure integer positions
    const finalX = Math.round(clampedX);
    const finalY = Math.round(clampedY);

    return { x: finalX, y: finalY };
  };

  // Determine if this element is selected
  const isSelected = selectedElement && selectedElement.id === element.id;

  return (
    <Group
      x={Math.round(x)}
      y={Math.round(y)}
      draggable={!render && editAllowed}
      onClick={() => setSelectedElement(element)}
      onTap={() => setSelectedElement(element)}
      dragBoundFunc={(pos) => {
        const { x: desiredX, y: desiredY } = pos;

        // Calculate clamped position
        const clamped = calculateClampedPosition(desiredX / scale, desiredY / scale);

        return { x: clamped.x * scale, y: clamped.y * scale };
      }}
      onDragEnd={(e) => {
        const group = e.target;
        const newX = Math.round(group.x());
        const newY = Math.round(group.y());

        // Calculate clamped and snapped positions
        const clamped = calculateClampedPosition(newX, newY);

        // Update element's position in unscaled units
        updateElement(element.id, 'x', clamped.x);
        updateElement(element.id, 'y', clamped.y);

        // Reset the group's position to clamped positions
        group.position({
          x: clamped.x,
          y: clamped.y,
        });
      }}
      aria-label="Bar Graph Indicator"
    >
      {/* Render Bars */}
      {Array.from({ length: numberOfBars }, (_, i) => {
        let barX = 0;
        let barY = 0;
        let currentBarWidth = barWidth;
        let currentBarHeight = barHeight;

        if (rotation === 0) {
          // Vertical Bars
          barX = i * (barWidth + margin);
          barY = 0;
          currentBarWidth = barWidth;
          currentBarHeight = barHeight;
        } else if (rotation === 90) {
          // Horizontal Bars with Low Value at Left
          barX = 0;
          barY = totalHeight - barWidth - i * (barWidth + margin);
          currentBarWidth = barHeight;
          currentBarHeight = barWidth;
        }

        const barFillColor = i < filledBars ? colorPalette[i] : disabledColor;

        return (
          <Rect
            key={i}
            x={Math.round(barX)}
            y={Math.round(barY)}
            width={Math.round(currentBarWidth)}
            height={Math.round(currentBarHeight)}
            fill={barFillColor}
          />
        );
      })}

      {!render && (
        <Rect
          x={0}
          y={0}
          width={Math.round(totalWidth)}
          height={Math.round(totalHeight)}
          stroke="#1976d2"
          strokeWidth={0.5}
          dash={isSelected ? [1, 1] : [0, 0]}
        />
      )}
    </Group>
  );
};

ThemeBar.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    usage: PropTypes.string.isRequired,
    barWidth: PropTypes.number.isRequired,
    barHeight: PropTypes.number.isRequired,
    margin: PropTypes.number.isRequired,
    numberOfBars: PropTypes.number.isRequired,
    startColor: PropTypes.string.isRequired,
    endColor: PropTypes.string.isRequired,
    disabledColor: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    startValue: PropTypes.number.isRequired,
    endValue: PropTypes.number.isRequired,
    testValue: PropTypes.number.isRequired,
    rotation: PropTypes.number.isRequired,
  }).isRequired,
  setSelectedElement: PropTypes.func.isRequired,
  updateElement: PropTypes.func.isRequired,
  gridSize: PropTypes.number.isRequired,          // Grid size for snapping
  snapToGridEnabled: PropTypes.bool.isRequired,    // Flag to enable snap to grid
  screenWidth: PropTypes.number.isRequired,        // Canvas width in unscaled units (e.g., 480)
  screenHeight: PropTypes.number.isRequired,       // Canvas height in unscaled units (e.g., 320)
  render: PropTypes.bool.isRequired,
  editAllowed: PropTypes.bool,
};

export default ThemeBar;
